import { Head, useForm } from "@inertiajs/react"
import { Camera } from "lucide-react"
import { useRef, useState } from "react"

import CountryCombobox from "@/components/country-combobox"
import DatePicker from "@/components/date-picker"
import DivisionCombobox from "@/components/division-combobox"
import FileUpload from "@/components/file-upload"
import LocationSelect from "@/components/location-select"
import PositionPicker from "@/components/position-picker"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { POSITIONS } from "@/lib/positions"
import type RequestsSetupPlayerProfile from "@/types/serializers/RequestsSetupPlayerProfile"

const DOMINANT_FOOT = [
  { value: "derecho", label: "Derecho" },
  { value: "izquierdo", label: "Izquierdo" },
  { value: "ambidiestro", label: "Ambidiestro" },
]

const AVAILABILITY = [
  { value: "disponible", label: "Disponible" },
  { value: "no_disponible", label: "No disponible" },
  { value: "escuchando_ofertas", label: "Escuchando ofertas" },
]

const STEPS = [
  "Datos básicos",
  "Info deportiva",
  "Situación actual",
  "Sobre mí",
  "Video CV",
]

export default function PlayerSetup() {
  const [step, setStep] = useState(0)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({})
  const prevCountryRef = useRef("")
  const { data, setData, patch, processing, errors } =
    useForm<RequestsSetupPlayerProfile>({
      player_profile: {
        date_of_birth: "",
        nationality: "",
        country: "",
        region: "",
        city: "",
        position: "",
        secondary_position: "",
        dominant_foot: "",
        height_cm: null,
        weight_kg: null,
        current_club: "",
        current_division: "",
        availability: "disponible",
        bio: "",
        idol: "",
        avatar: null,
        video_cv: null,
      },
    })

  const setField = (
    field: keyof typeof data.player_profile,
    value: string | number | null,
  ) =>
    setData("player_profile", {
      ...data.player_profile,
      [field]: value,
    } as typeof data.player_profile)

  function handleCountryChange(newCountry: string) {
    const shouldAutoFill =
      data.player_profile.nationality === "" ||
      data.player_profile.nationality === prevCountryRef.current
    setData("player_profile", {
      ...data.player_profile,
      country: newCountry,
      region: "",
      city: "",
      ...(shouldAutoFill ? { nationality: newCountry } : {}),
    })
    prevCountryRef.current = newCountry
  }

  function handleRegionChange(newRegion: string) {
    setData("player_profile", {
      ...data.player_profile,
      region: newRegion,
      city: "",
    })
  }

  function validateStep(): boolean {
    const newErrors: Record<string, string> = {}

    if (step === 0) {
      if (!data.player_profile.date_of_birth) {
        newErrors.date_of_birth = "La fecha de nacimiento es requerida"
      }
      if (!data.player_profile.country) {
        newErrors.country = "El país es requerido"
      }
    } else if (step === 1) {
      if (!data.player_profile.position) {
        newErrors.position = "La posición es requerida"
      }
      if (!data.player_profile.dominant_foot) {
        newErrors.dominant_foot = "El pie dominante es requerido"
      }
    }

    setStepErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  function handleNext() {
    if (!validateStep()) return
    if (step < STEPS.length - 1) {
      setStep(step + 1)
    }
  }

  function handleBack() {
    setStepErrors({})
    if (step > 0) {
      setStep(step - 1)
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (step < STEPS.length - 1) return
    patch("/perfil/completar")
  }

  return (
    <>
      <Head title="Completar perfil" />

      <div className="bg-background flex min-h-svh flex-col items-center p-6">
        <img
          src="/images/krown_logo_header.png"
          alt="KrownFT"
          className="mb-6 h-10"
        />

        <div className="w-full max-w-lg">
          {/* Progress */}
          <div className="mb-6 text-center">
            <p className="text-krown-50/70 text-sm">
              Paso {step + 1} de {STEPS.length}
            </p>
            <h1 className="mt-1 text-xl font-semibold text-white">
              {STEPS[step]}
            </h1>
            <div className="mt-3 flex gap-1">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full ${i <= step ? "bg-krown-accent" : "bg-krown-600"}`}
                />
              ))}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-card rounded-lg p-6 shadow-lg"
          >
            {/* Step 1: Datos básicos */}
            {step === 0 && (
              <div className="space-y-4">
                {/* Avatar upload */}
                <div
                  data-testid="avatar-upload-area"
                  className="flex flex-col items-center gap-3"
                >
                  <Label className="text-center">Foto de perfil</Label>
                  {avatarPreview ? (
                    <Avatar className="size-24">
                      <AvatarImage
                        src={avatarPreview}
                        alt="Vista previa"
                        className="object-cover"
                      />
                      <AvatarFallback>
                        <Camera className="text-muted-foreground size-8" />
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <div className="bg-secondary flex size-24 items-center justify-center rounded-full">
                      <Camera className="text-muted-foreground size-8" />
                    </div>
                  )}
                  <FileUpload
                    accept="image"
                    onUploadComplete={(signedId, file) => {
                      setField("avatar", signedId)
                      const reader = new FileReader()
                      reader.onload = (e) =>
                        setAvatarPreview(e.target?.result as string)
                      reader.readAsDataURL(file)
                    }}
                  />
                  <p className="text-muted-foreground text-xs">
                    Opcional — podés agregar tu foto después
                  </p>
                </div>

                <DatePicker
                  label="Fecha de nacimiento"
                  value={data.player_profile.date_of_birth}
                  onChange={(v) => setField("date_of_birth", v)}
                  error={
                    stepErrors.date_of_birth ||
                    errors["player_profile.date_of_birth"]?.[0]
                  }
                />
                <LocationSelect
                  country={data.player_profile.country}
                  region={data.player_profile.region ?? ""}
                  city={data.player_profile.city ?? ""}
                  onCountryChange={handleCountryChange}
                  onRegionChange={handleRegionChange}
                  onCityChange={(v) => setField("city", v)}
                  errors={{
                    country:
                      stepErrors.country ||
                      errors["player_profile.country"]?.[0],
                  }}
                />
                <CountryCombobox
                  label="Nacionalidad"
                  value={data.player_profile.nationality ?? ""}
                  onChange={(v) => setField("nationality", v)}
                  placeholder="Seleccionar nacionalidad..."
                  error={errors["player_profile.nationality"]?.[0]}
                />
              </div>
            )}

            {/* Step 2: Info deportiva */}
            {step === 1 && (
              <div className="space-y-4">
                {/* Position picker - visual field on normal screens, dropdown fallback on very small screens */}
                <div className="space-y-1.5">
                  <Label>Posición</Label>
                  <div className="mt-2 hidden min-[320px]:block">
                    <PositionPicker
                      value={data.player_profile.position || null}
                      onChange={(val) => setField("position", val ?? "")}
                      secondaryValue={
                        data.player_profile.secondary_position || null
                      }
                      onSecondaryChange={(val) =>
                        setField("secondary_position", val ?? "")
                      }
                    />
                    <p className="text-muted-foreground mt-1 text-center text-xs">
                      Tocá para elegir posición principal. Tocá otra para
                      secundaria.
                    </p>
                  </div>
                  {/* Dropdown fallback for very small screens (<320px) */}
                  <div className="space-y-4 min-[320px]:hidden">
                    <Select
                      value={data.player_profile.position}
                      onValueChange={(val) => setField("position", val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Posición principal" />
                      </SelectTrigger>
                      <SelectContent>
                        {POSITIONS.map((pos) => (
                          <SelectItem key={pos.value} value={pos.value}>
                            {pos.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={
                        data.player_profile.secondary_position ?? undefined
                      }
                      onValueChange={(val) =>
                        setField("secondary_position", val)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Posición secundaria (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {POSITIONS.map((pos) => (
                          <SelectItem key={pos.value} value={pos.value}>
                            {pos.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(stepErrors.position ||
                    errors["player_profile.position"]) && (
                    <p className="mt-1 text-sm text-red-500">
                      {stepErrors.position || errors["player_profile.position"]}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Pie dominante</Label>
                  <Select
                    value={data.player_profile.dominant_foot}
                    onValueChange={(val) => setField("dominant_foot", val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccioná tu pie" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOMINANT_FOOT.map((foot) => (
                        <SelectItem key={foot.value} value={foot.value}>
                          {foot.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(stepErrors.dominant_foot ||
                    errors["player_profile.dominant_foot"]) && (
                    <p className="mt-1 text-sm text-red-500">
                      {stepErrors.dominant_foot ||
                        errors["player_profile.dominant_foot"]}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="height_cm">Altura (cm)</Label>
                    <Input
                      id="height_cm"
                      type="number"
                      value={data.player_profile.height_cm ?? ""}
                      onChange={(e) => setField("height_cm", e.target.value)}
                      placeholder="175"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="weight_kg">Peso (kg)</Label>
                    <Input
                      id="weight_kg"
                      type="number"
                      value={data.player_profile.weight_kg ?? ""}
                      onChange={(e) => setField("weight_kg", e.target.value)}
                      placeholder="72"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Situación actual */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="current_club">Club actual</Label>
                  <Input
                    id="current_club"
                    value={data.player_profile.current_club ?? ""}
                    onChange={(e) => setField("current_club", e.target.value)}
                    placeholder="Ej: Club Atlético Rosario Central"
                  />
                </div>
                <DivisionCombobox
                  country={data.player_profile.country}
                  value={data.player_profile.current_division ?? ""}
                  onChange={(v) => setField("current_division", v)}
                  error={errors["player_profile.current_division"]?.[0]}
                />
                <div className="space-y-1.5">
                  <Label>Disponibilidad</Label>
                  <Select
                    value={data.player_profile.availability}
                    onValueChange={(val) => setField("availability", val)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AVAILABILITY.map((a) => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 4: Sobre mí */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="bio">Biografía</Label>
                  <Textarea
                    id="bio"
                    value={data.player_profile.bio ?? ""}
                    onChange={(e) => setField("bio", e.target.value)}
                    placeholder="Contá un poco sobre vos..."
                    maxLength={500}
                    rows={4}
                  />
                  <p className="mt-1 text-right text-xs text-neutral-500">
                    {String(data.player_profile.bio).length}/500
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="idol">Ídolo / Referente</Label>
                  <Input
                    id="idol"
                    value={data.player_profile.idol ?? ""}
                    onChange={(e) => setField("idol", e.target.value)}
                    placeholder="Ej: Lionel Messi"
                  />
                </div>
              </div>
            )}

            {/* Step 5: Video CV */}
            {step === 4 && (
              <div className="space-y-4">
                <p className="text-muted-foreground text-center">
                  Podés subir un video con tus mejores jugadas. También podés
                  hacerlo después.
                </p>
                <FileUpload
                  accept="video"
                  onUploadComplete={(signedId) => {
                    setField("video_cv", signedId)
                  }}
                />
              </div>
            )}

            {/* Navigation buttons */}
            <div className="mt-6 flex justify-between">
              {step > 0 ? (
                <Button type="button" variant="outline" onClick={handleBack}>
                  Anterior
                </Button>
              ) : (
                <div />
              )}

              {step < STEPS.length - 1 ? (
                <Button type="button" onClick={handleNext}>
                  Siguiente
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={processing}
                  className="bg-krown-accent text-krown-800 hover:bg-krown-accent/90"
                >
                  Completar perfil
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
